/**
 * AliExpress Product Detail Page
 *
 * Shows detailed product information with buy link to AliExpress
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  ExternalLink,
  Star,
  Truck,
  Shield,
  TrendingUp,
  ShoppingCart,
  Heart,
  Share2,
  AlertCircle,
  PackageCheck,
  Clock
} from 'lucide-react';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { estimateKrwPrice } from '../lib/aliexpress';
import type { AliExpressProductDetail, AliExpressHotProduct } from '../types';

// Extend the detail type to include productUrl from search results
interface ProductData extends AliExpressProductDetail {
  productUrl?: string;
}

export function AliExpressProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [product, setProduct] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLiked, setIsLiked] = useState(false);

  // Get product data from navigation state (passed from product list)
  const locationState = location.state as { product?: AliExpressHotProduct } | null;

  useEffect(() => {
    async function fetchProduct() {
      if (!id) return;

      // First, check if we have product data from navigation state
      if (locationState?.product) {
        const p = locationState.product;
        setProduct({
          productId: p.productId,
          title: p.productTitle,
          description: '',
          images: p.imageUrl ? [p.imageUrl] : [],
          imageUrl: p.imageUrl || '',
          productUrl: p.productUrl || `https://www.aliexpress.com/item/${p.productId}.html`,
          videos: [],
          originalPrice: p.originalPrice,
          salePrice: p.salePrice,
          currency: 'USD',
          discount: p.discount || '',
          stock: 999,
          soldCount: p.volume || 0,
          rating: p.evaluateScore || 0,
          reviewCount: 0,
          favoriteCount: 0,
          specs: [],
          shippingOptions: [],
          shopInfo: {
            shopId: p.shopId || '',
            shopName: p.shopName || '',
            shopUrl: p.shopUrl || '',
            shopLogo: '',
            rating: 0,
            followerCount: 0,
            establishedYear: 0,
            positiveFeedbackRate: 0
          },
          categoryPath: [p.categoryName || ''],
          tags: [],
          warranty: '',
          returnPolicy: ''
        });
        setLoading(false);
        return;
      }

      // Fallback: fetch from API
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`https://moapc-worker.linuxm.workers.dev/api/aliexpress/product/${id}`);

        if (!response.ok) {
          throw new Error('제품을 찾을 수 없습니다');
        }

        const data = await response.json();
        setProduct(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : '제품을 불러올 수 없습니다');
      } finally {
        setLoading(false);
      }
    }

    fetchProduct();
  }, [id, locationState]);

  const handleBuyClick = () => {
    if (product && typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'aliexpress_purchase_click', {
        product_id: product.productId,
        product_name: product.title,
        price: product.salePrice
      });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product?.title,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('링크가 복사되었습니다!');
    }
  };

  if (loading) {
    return (
      <div className="container py-12 flex flex-col items-center justify-center gap-4">
        <LoadingSpinner size="lg" />
        <p className="text-gray-600">제품 정보를 불러오는 중...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container py-12 text-center">
        <Package size={48} className="mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          제품을 찾을 수 없습니다
        </h2>
        <p className="text-gray-600 mb-4">{error || '요청하신 제품 정보를 찾을 수 없습니다.'}</p>
        <Link
          to="/products"
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          <ArrowLeft size={18} />
          제품 목록으로 이동
        </Link>
      </div>
    );
  }

  const krwPrice = estimateKrwPrice(product.salePrice);
  const originalKrwPrice = product.originalPrice > product.salePrice
    ? estimateKrwPrice(product.originalPrice)
    : null;
  const discountPercent = originalKrwPrice
    ? Math.round((1 - product.salePrice / product.originalPrice) * 100)
    : 0;

  // Handle images - use imageUrl as fallback if images array is empty
  const images: string[] = [];
  if (product.images && product.images.length > 0) {
    images.push(...product.images.filter(img => img && img.length > 0));
  }
  if (product.imageUrl && !images.includes(product.imageUrl)) {
    images.unshift(product.imageUrl);
  }
  // Ensure at least one image
  if (images.length === 0) {
    images.push(''); // Will show placeholder
  }

  const currentImage = images[selectedImage] || images[0];

  // Generate search URL if productUrl is a mock URL
  const getRealProductUrl = () => {
    const url = product.productUrl || '';
    // Check if it's a mock URL (contains mock_ or numeric ID pattern)
    if (url.includes('/item/mock_') || url.match(/\/item\/\d+\.html$/)) {
      // Use search instead for mock products
      return `https://www.aliexpress.com/wholesale?SearchText=${encodeURIComponent(product.title)}`;
    }
    return url || `https://www.aliexpress.com/wholesale?SearchText=${encodeURIComponent(product.title)}`;
  };
  const productUrl = getRealProductUrl();

  // Placeholder image generator
  const getPlaceholderImage = () => {
    return `data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
        <rect fill="#FFF7ED" width="400" height="400"/>
        <text fill="#FB923C" font-family="Arial" font-size="18" font-weight="bold" x="50%" y="45%" text-anchor="middle" dominant-baseline="middle">Product Image</text>
        <text fill="#9CA3AF" font-family="Arial" font-size="14" x="50%" y="55%" text-anchor="middle" dominant-baseline="middle">Not Available</text>
      </svg>
    `)}`;
  };

  return (
    <div className="container py-8 max-w-6xl mx-auto">
      {/* Breadcrumb */}
      <nav className="mb-4 text-sm">
        <ol className="flex items-center gap-2 text-gray-600 flex-wrap">
          <li>
            <Link to="/" className="hover:text-orange-600 transition-colors">
              홈
            </Link>
          </li>
          <li className="text-gray-400">/</li>
          <li>
            <Link to="/products" className="hover:text-orange-600 transition-colors">
              제품
            </Link>
          </li>
          <li className="text-gray-400">/</li>
          <li className="text-gray-900 font-medium truncate max-w-xs">
            {product.title || '제품 상세'}
          </li>
        </ol>
      </nav>

      {/* Back Button */}
      <Link
        to="/products"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 mb-6 transition-colors"
      >
        <ArrowLeft size={18} />
        <span>목록으로 이동</span>
      </Link>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          {/* Left: Image Gallery */}
          <div className="p-6 bg-gray-50">
            {/* Main Image */}
            <div className="aspect-square bg-white rounded-xl overflow-hidden relative shadow-sm">
              <img
                src={currentImage || getPlaceholderImage()}
                alt={product.title || 'Product'}
                className="w-full h-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (!target.src.startsWith('data:image')) {
                    target.src = getPlaceholderImage();
                  }
                }}
              />
              {discountPercent > 0 && (
                <div className="absolute top-4 left-4 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full shadow">
                  {discountPercent}% 할인
                </div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === idx
                        ? 'border-orange-500 ring-2 ring-orange-200'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <img
                      src={img || getPlaceholderImage()}
                      alt={`${product.title} ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (!target.src.startsWith('data:image')) {
                          target.src = getPlaceholderImage();
                        }
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Product Info */}
          <div className="p-6 lg:p-8">
            {/* Store Badge */}
            <div className="flex items-center gap-2 mb-3">
              <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                알리익스프레스
              </span>
              {product.shopInfo?.shopName && (
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {product.shopInfo.shopName}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight mb-4">
              {product.title || '제품 정보'}
            </h1>

            {/* Rating & Sales */}
            <div className="flex items-center gap-4 mb-4">
              {product.rating > 0 && (
                <div className="flex items-center gap-1">
                  <Star size={16} className="text-yellow-400 fill-yellow-400" />
                  <span className="font-medium text-gray-900">{product.rating.toFixed(1)}</span>
                  {product.reviewCount > 0 && (
                    <span className="text-gray-500 text-sm">({product.reviewCount} 리뷰)</span>
                  )}
                </div>
              )}
              {product.soldCount > 0 && (
                <div className="flex items-center gap-1 text-gray-600">
                  <TrendingUp size={16} className="text-green-500" />
                  <span className="text-sm">{product.soldCount.toLocaleString()}개 판매</span>
                </div>
              )}
            </div>

            {/* Price Box */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-5 mb-6 border border-orange-100">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-3xl font-bold text-orange-600">
                  ₩{krwPrice.toLocaleString()}
                </span>
                {originalKrwPrice && (
                  <span className="text-lg text-gray-400 line-through">
                    ₩{originalKrwPrice.toLocaleString()}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                * 알리익스프레스 판매가 (원화 환산)
              </p>
            </div>

            {/* Category */}
            {product.categoryPath && product.categoryPath.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <span>카테고리:</span>
                <span className="text-orange-600">{product.categoryPath.join(' > ')}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 mb-6">
              <a
                href={productUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleBuyClick}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-700 transition-colors"
              >
                <ShoppingCart size={20} />
                알리에서 구매하기
                <ExternalLink size={16} />
              </a>
              <button
                onClick={() => setIsLiked(!isLiked)}
                className={`p-3.5 rounded-xl border transition-colors ${
                  isLiked
                    ? 'bg-red-50 border-red-200 text-red-500'
                    : 'bg-white border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200'
                }`}
              >
                <Heart size={20} fill={isLiked ? 'currentColor' : 'none'} />
              </button>
              <button
                onClick={handleShare}
                className="p-3.5 rounded-xl border border-gray-200 text-gray-400 hover:text-orange-500 hover:border-orange-200 transition-colors"
              >
                <Share2 size={20} />
              </button>
            </div>

            {/* Shipping Info */}
            <div className="bg-blue-50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 text-blue-800 font-medium mb-2">
                <Truck size={18} />
                배송 안내
              </div>
              <ul className="text-sm text-blue-700 space-y-1">
                <li className="flex items-center gap-2">
                  <Clock size={14} />
                  예상 배송: 2-4주
                </li>
                <li className="flex items-center gap-2">
                  <PackageCheck size={14} />
                  한국 직배송 가능
                </li>
              </ul>
            </div>

            {/* Specs */}
            {product.specs && product.specs.length > 0 && (
              <div className="border rounded-xl overflow-hidden mb-4">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h3 className="font-semibold text-gray-900 text-sm">제품 사양</h3>
                </div>
                <div className="divide-y">
                  {product.specs.slice(0, 5).map((spec, idx) => (
                    <div key={idx} className="flex px-4 py-2.5 text-sm">
                      <span className="w-1/3 text-gray-500">{spec.name}</span>
                      <span className="w-2/3 text-gray-900">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Info Cards */}
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {/* Import Tax Info */}
        <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
          <div className="flex items-center gap-2 text-amber-800 font-medium mb-3">
            <AlertCircle size={18} />
            관부가세 안내
          </div>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>• $200(중국) 초과 시 관부가세 부과</li>
            <li>• $150(미국) 초과 시 관부가세 부과</li>
            <li>• 실제 과세 여부는 세관 확인 필요</li>
          </ul>
        </div>

        {/* Safe Shopping Info */}
        <div className="bg-green-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center gap-2 text-green-800 font-medium mb-3">
            <Shield size={18} />
            안전 쇼핑 안내
          </div>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• 알리익스프레스 공식 결제 시스템</li>
            <li>• 구매자 보호 프로그램 적용</li>
            <li>• 배송 추적 가능</li>
          </ul>
        </div>
      </div>

      {/* Tags */}
      {product.tags && product.tags.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">태그</h3>
          <div className="flex flex-wrap gap-2">
            {product.tags.map((tag, idx) => (
              <span
                key={idx}
                className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full hover:bg-gray-200 cursor-pointer transition-colors"
              >
                #{tag}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AliExpressProductDetailPage;
